/**
 * Submission status — SPEC §3.2, §3.3, §3.7.
 *
 * ## Only two things are stored
 *
 * `DRAFT` and `SUBMITTED`. Everything an admin sees — NO_RESPONSE, PARTIAL, COMPLETE,
 * PENDING_REMAINDER, ON_LEAVE — is derived from those plus the instructor's confirmation
 * horizon, any leave of absence, and the calendar.
 *
 * The horizon arrives as a separate input rather than as a field on the submission, because
 * it belongs to the **person** and spans forward across every month (SPEC §3.3.1). Holding it
 * on the month was what allowed "unsure past September 15" to coexist with "all of October
 * confirmed".
 *
 * SPEC §5 listed PENDING_REMAINDER as a stored value. Deriving it instead is a deliberate
 * refinement: it is a pure function of the horizon and the month's last day, so
 * storing it would create a second copy that drifts the moment someone edits one and not
 * the other. The same argument applies to NO_RESPONSE (absence of a row) and ON_LEAVE (a
 * row in another table). Status is a view, not a fact.
 *
 * ## The reminder rule this exists to protect
 *
 * A submitted month with two available days is COMPLETE, not PARTIAL. SPEC §3.2 is explicit
 * that low availability is a coverage signal for the dashboard and never a reminder trigger
 * — nagging someone who honestly reported being busy is how you teach people to ignore
 * your email.
 */
import { compare, parseISO, } from '../dates/calendar.js';
import { firstDayOf, lastDayOf } from '../cycle/cycle.js';
import { firstUnconfirmedDay, horizonFallsInside } from './horizon.js';
/** Effective last day of a leave, accounting for an early end. */
function leaveEndsOn(leave) {
    const end = parseISO(leave.endDate);
    if (!leave.endedOn)
        return end;
    const ended = parseISO(leave.endedOn);
    return compare(ended, end) < 0 ? ended : end;
}
/**
 * True when a leave covers the cycle's month **entirely**.
 *
 * Partial overlap deliberately does not count. Someone away for the first ten days of March
 * can still tell us about the rest of March, and silencing the reminder would lose the
 * whole month rather than part of it.
 */
/**
 * True when a single DAY falls inside a leave.
 *
 * The per-month rule above answers "should we chase them for this month". This answers
 * "can a class be built on this day", and the two are genuinely different questions:
 * somebody away 10-30 November is not ON_LEAVE for November — they can still tell us about
 * the first week — but the 20th is not cover, and counting it would put a class on somebody
 * who is not there.
 *
 * Whole-month leave is the easy case. Partial leave is the one that quietly breaks planning,
 * because the month looks normal on every screen that only asks the monthly question.
 */
export function isOnLeaveOn(date, leaves) {
    const day = parseISO(date);
    return leaves.some((l) => compare(parseISO(l.startDate), day) <= 0 && compare(leaveEndsOn(l), day) >= 0);
}
export function isOnLeaveFor(cycleKey, leaves) {
    const first = firstDayOf(cycleKey);
    const last = lastDayOf(cycleKey);
    return leaves.some((l) => compare(parseISO(l.startDate), first) <= 0 && compare(leaveEndsOn(l), last) >= 0);
}
export function deriveStatus({ cycleKey, submission, leaves = [], horizon = null, }) {
    // Leave wins over everything: an instructor who told us they are away has answered,
    // and must not appear as a non-responder.
    if (isOnLeaveFor(cycleKey, leaves))
        return 'ON_LEAVE';
    if (!submission)
        return 'NO_RESPONSE';
    if (submission.state === 'DRAFT') {
        return submission.hasContent ? 'PARTIAL' : 'NO_RESPONSE';
    }
    // SUBMITTED.
    //
    // Only a horizon landing INSIDE this month leaves it part-confirmed. A horizon sitting
    // before the month is overridden by the act of submitting it — that is the instructor
    // saying "I do know October" (SPEC §3.3.1), and reporting it as PENDING_REMAINDER would
    // chase them for a month they had just confirmed.
    if (horizonFallsInside(horizon, cycleKey)) {
        if (compare(parseISO(horizon), lastDayOf(cycleKey)) < 0)
            return 'PENDING_REMAINDER';
    }
    return 'COMPLETE';
}
/**
 * The window a remainder request should ask about — the part of the month the instructor
 * has not yet confirmed (SPEC §3.3, §6).
 *
 * Returns null when nothing is outstanding. Never re-requests what they already gave.
 */
export function remainderWindow(cycleKey, submission, horizon = null) {
    if (!submission || submission.state !== 'SUBMITTED')
        return null;
    const ctx = { cycleKey, horizon, submitted: true };
    const from = firstUnconfirmedDay(ctx);
    return from ? { from, to: lastDayOf(cycleKey) } : null;
}
/** Statuses that mean "this instructor still owes us something for this cycle". */
export const OUTSTANDING = [
    'NO_RESPONSE',
    'PARTIAL',
    'PENDING_REMAINDER',
];
export function isOutstanding(status) {
    return OUTSTANDING.includes(status);
}
/** Plain-language label. Instructors never see the words cycle, submission, or partial (SPEC §12.2). */
export function instructorLabel(status, monthName) {
    switch (status) {
        case 'COMPLETE':
            return `${monthName} is confirmed`;
        case 'PENDING_REMAINDER':
            return `${monthName} is partly confirmed`;
        case 'PARTIAL':
            return `${monthName} is saved but not sent`;
        case 'ON_LEAVE':
            return `You're on a break through ${monthName}`;
        case 'NO_RESPONSE':
            return `${monthName} still needs your availability`;
    }
}
//# sourceMappingURL=status.js.map